import React, { useEffect, useMemo, useRef } from "react";
import { theme, ConfigProvider, Layout, Spin } from "antd";
import RolesSidebar from "./common/RolesSidebar";
import FormBuilder from "./builder/FormBuilder";
import CreateRoleModal from "./modals/CreateRoleModal";
import useClinicalRoles from "../../../../../hooks/useClinicalRoles";
import useClinicalForms from "../../../../../hooks/useClinicalForms";
import useUrlParam from "../../../../../hooks/useUrlParam";
import {
  withDefaultOpRoleForms,
  withDefaultRoleForms,
} from "../../../../../domain/clinicalForms";

const isPersistedId = (id) => /^\d+$/.test(String(id));

// URL-friendly key for a role: builtin enum value (e.g. "Doctor") or numeric id.
const roleUrlKey = (role) => (role?.system ? role.value : role?.id);
const findRoleByUrlKey = (roles, key) =>
  roles.find((r) => roleUrlKey(r) === key);

const seedForRole = (role, forms) => {
  if (!role.system) return withDefaultRoleForms(forms);
  if (role.value === "Nurse") return forms;
  return withDefaultOpRoleForms(forms);
};

const DynamicRolesAndForms = ({ createOpen, onCreateOpenChange }) => {
  const { token } = theme.useToken();
  const {
    roles: allRoles,
    loading,
    creating,
    create,
    updateLocal,
  } = useClinicalRoles();
  const { listForms } = useClinicalForms();
  const [roleKey, setRoleKey] = useUrlParam("role", { replace: true });
  const hydratedRef = useRef(new Set());

  const roles = useMemo(
    () => allRoles.filter((r) => r.value !== "Receptionist"),
    [allRoles]
  );

  const activeRole = useMemo(() => {
    const fromUrl = findRoleByUrlKey(roles, roleKey);
    if (fromUrl) return fromUrl;
    const firstCustom = roles.find((r) => !r.system);
    return firstCustom || roles[0];
  }, [roles, roleKey]);

  const selectRole = (role) =>
    setRoleKey(roleUrlKey(role), { clear: ["form"] });

  useEffect(() => {
    if (!activeRole) return;
    if (hydratedRef.current.has(activeRole.id)) return;
    hydratedRef.current.add(activeRole.id);

    const params = isPersistedId(activeRole.id)
      ? { clinical_role: Number(activeRole.id) }
      : { role: activeRole.value };

    listForms(params)
      .then((forms) => {
        const nextForms = seedForRole(activeRole, forms || []);
        if (nextForms.length) {
          updateLocal({ ...activeRole, forms: nextForms });
        }
      })
      .catch(() => {
        hydratedRef.current.delete(activeRole.id);
      });
  }, [activeRole, listForms, updateLocal]);

  const handleCreate = async (input) => {
    const created = await create(input).catch(() => null);
    if (created) {
      selectRole(created);
      onCreateOpenChange?.(false);
    }
  };

  const primaryTint = `color-mix(in srgb, ${token.colorPrimary} 14%, white)`;
  const primaryBorder = `color-mix(in srgb, ${token.colorPrimary} 35%, white)`;
  const themeVars = {
    "--drf-primary": token.colorPrimary,
    "--drf-primary-tint": primaryTint,
    "--drf-primary-border": primaryBorder,
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Segmented: {
            itemSelectedBg: primaryTint,
            itemSelectedColor: token.colorPrimary,
            itemHoverColor: token.colorPrimary,
            trackBg: "#f9fafb",
          },
          Menu: {
            itemSelectedBg: primaryTint,
            itemSelectedColor: token.colorPrimary,
            itemHoverColor: token.colorPrimary,
          },
        },
      }}
    >
      <div style={themeVars}>
        <Layout
          style={{
            height: "calc(100vh - 200px)",
            marginBottom: -32,
            background: "#ffffff",
            borderTop: "1px solid #eaecf0",
          }}
        >
          <Layout.Sider
            width={248}
            theme="light"
            style={{
              borderRight: "1px solid #eaecf0",
              padding: "20px 16px",
              overflowY: "auto",
            }}
          >
            <Spin spinning={loading}>
              <RolesSidebar
                roles={roles}
                activeId={activeRole?.id}
                onSelect={(id) => {
                  const role = roles.find((r) => r.id === id);
                  if (role) selectRole(role);
                }}
              />
            </Spin>
          </Layout.Sider>
          <Layout.Content
            style={{
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              minHeight: 0,
            }}
          >
            {activeRole && (
              <FormBuilder
                role={activeRole}
                onChange={updateLocal}
                roles={roles}
                updateRole={updateLocal}
              />
            )}
          </Layout.Content>
        </Layout>
        <CreateRoleModal
          open={!!createOpen}
          loading={creating}
          onCancel={() => onCreateOpenChange?.(false)}
          onCreate={handleCreate}
        />
      </div>
    </ConfigProvider>
  );
};

export default DynamicRolesAndForms;
